import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { PageHeader } from '@/components/layout/PageHeader'
import { Breadcrumb } from '@/components/layout/Breadcrumb'
import { Button } from '@/components/ui/Button'
import { PageError } from '@/components/ui/PageAsyncState'
import { FormField } from '@/components/forms/FormField'
import {
  MATERIAL_KIND_LABEL,
  materialService,
  type MaterialKind,
} from '@/services/materials'
import { normalizeUomLabel } from '@/lib/uom'

const inputClass =
  'w-full rounded-[var(--radius-sm)] border border-[var(--color-border)] bg-white px-2.5 py-1.5 text-sm outline-none focus:border-[var(--color-accent)]'

type FormState = {
  code: string
  name: string
  kind: '' | MaterialKind
  uom_default: string
  notes: string
}

const emptyForm = (): FormState => ({
  code: '',
  name: '',
  kind: '',
  uom_default: '',
  notes: '',
})

function Section({
  title,
  children,
}: {
  title: string
  children: React.ReactNode
}) {
  return (
    <section className="border border-[var(--color-border)] bg-white">
      <div className="border-b border-[var(--color-border)] bg-slate-50 px-3 py-2 text-xs font-semibold tracking-wide text-slate-600 uppercase">
        {title}
      </div>
      <div className="space-y-3 p-3 sm:p-4">{children}</div>
    </section>
  )
}

export function MaterialFormPage() {
  const navigate = useNavigate()
  const [form, setForm] = useState<FormState>(emptyForm)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    if (!form.kind) {
      setError('kind bắt buộc (RAW hoặc AUX).')
      return
    }
    setSaving(true)
    try {
      await materialService.create({
        code: form.code,
        name: form.name,
        kind: form.kind,
        status: 'active',
        uom_default: form.uom_default ? normalizeUomLabel(form.uom_default) : undefined,
        notes: form.notes || undefined,
      })
      navigate('/purchasing/materials')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Không thể tạo Material')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div>
      <Breadcrumb
        items={[
          { label: 'Trang chủ', to: '/' },
          { label: 'Mua hàng', to: '/purchasing' },
          { label: 'Vật tư (Material)', to: '/purchasing/materials' },
          { label: 'Material mới' },
        ]}
      />
      <PageHeader
        title="Tạo Material"
        subtitle="Master vật tư — lưu Postgres qua API"
      />

      <form onSubmit={(e) => void handleSubmit(e)} className="max-w-2xl space-y-3">
        {error ? <PageError message={error} /> : null}

        <Section title="Thông tin Material">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <FormField label="Mã" required>
              <input
                required
                className={inputClass}
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value })}
                placeholder="MDF-18"
              />
            </FormField>
            <FormField label="Tên" required>
              <input
                required
                className={inputClass}
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="MDF 18mm"
              />
            </FormField>
            <FormField label="Loại" required>
              <select
                required
                className={inputClass}
                value={form.kind}
                onChange={(e) =>
                  setForm({
                    ...form,
                    kind: e.target.value as '' | MaterialKind,
                  })
                }
              >
                <option value="" disabled>
                  -- Chọn loại --
                </option>
                <option value="RAW">{MATERIAL_KIND_LABEL.RAW}</option>
                <option value="AUX">{MATERIAL_KIND_LABEL.AUX}</option>
              </select>
            </FormField>
            <FormField label="ĐVT mặc định">
              <input
                className={inputClass}
                value={form.uom_default}
                onChange={(e) =>
                  setForm({ ...form, uom_default: e.target.value })
                }
                onBlur={() => {
                  if (form.uom_default) {
                    setForm((prev) => ({
                      ...prev,
                      uom_default: normalizeUomLabel(prev.uom_default),
                    }))
                  }
                }}
                placeholder="TAM / CÁI / M3..."
              />
            </FormField>
            <div className="sm:col-span-2">
              <FormField label="Ghi chú">
                <textarea
                  rows={3}
                  className={inputClass}
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  placeholder="Ghi chú nội bộ..."
                />
              </FormField>
            </div>
          </div>
        </Section>

        <div className="flex justify-end gap-2 border border-[var(--color-border)] bg-slate-50 px-3 py-2.5">
          <Button
            type="button"
            variant="secondary"
            onClick={() => navigate('/purchasing/materials')}
            disabled={saving}
          >
            Hủy
          </Button>
          <Button type="submit" disabled={saving}>
            {saving ? 'Đang lưu...' : 'Lưu Material'}
          </Button>
        </div>
      </form>
    </div>
  )
}
